// src/services/msalAuthService.ts
// MSAL NAA (Nested App Authentication) service for Office Add-in SSO.
// Module-level singleton — initialized once, reused across token requests.
//
// NOTE (MSAL v5): prefer `error.errorCode` over `error.message` for branching —
// v5 error messages are doc-URL style, not descriptive text as in v4.
//
// Error taxonomy (exported for AuthContext to branch on via instanceof):
//   - `SilentTimeoutError`              - our outer timeout tripped; MSAL never returned
//   - `InteractionRequiredFallbackError` - silent auth needs user interaction (typically
//                                          missing tenant consent); we deliberately do not
//                                          prompt - caller falls back to Legacy SSO
//   - Anything else                     - unknown / unclassified; treat as token acquisition failure

import {
  createNestablePublicClientApplication,
  InteractionRequiredAuthError,
} from "@azure/msal-browser";
import type {
  IPublicClientApplication,
  AccountInfo,
  AuthenticationResult,
} from "@azure/msal-browser";
import {
  MSAL_CONFIG,
  MSAL_SCOPES,
  SSO_SILENT_TIMEOUT_MS,
  persistTenantId,
} from "../config/authConfig";
import { createLogger } from "../utils/logger";

/**
 * Our outer timeout wrapper tripped before MSAL returned.
 * AuthContext uses `instanceof` to classify this for telemetry.
 */
export class SilentTimeoutError extends Error {
  constructor(label: string, timeoutMs: number) {
    super(`${label} timed out after ${timeoutMs}ms`);
    this.name = "SilentTimeoutError";
  }
}

/**
 * Silent NAA token acquisition needs user interaction - in practice almost
 * always a tenant that has not (fully) admin-consented the app. We deliberately
 * do NOT open an interactive prompt: for non-admin users in most tenants the
 * consent dialog is a dead-end "Need admin approval" screen that reappears
 * every session, while Legacy SSO works silently as a fallback. Callers treat
 * this error as "fall back to Legacy SSO now"; telemetry records it per tenant
 * so we can see where admin consent is still missing.
 *
 * Carries the original MSAL diagnostic fields as own properties so
 * `extractMsalErrorFields` surfaces them unchanged for Azure sign-in log
 * cross-referencing. AuthContext uses `instanceof` to classify this for
 * telemetry.
 */
export class InteractionRequiredFallbackError extends Error {
  errorCode?: string;
  subError?: string;
  correlationId?: string;
  constructor(original: unknown) {
    const o = (original ?? {}) as Record<string, unknown>;
    super(
      typeof o.message === "string" && o.message
        ? o.message
        : "Silent authentication requires user interaction"
    );
    this.name = "InteractionRequiredFallbackError";
    if (typeof o.errorCode === "string") this.errorCode = o.errorCode;
    if (typeof o.subError === "string") this.subError = o.subError;
    if (typeof o.correlationId === "string") this.correlationId = o.correlationId;
  }
}

const log = createLogger("MsalAuthService");

let pcaInstance: IPublicClientApplication | null = null;
let activeAccount: AccountInfo | null = null;
let officeTenantId: string | null = null;

/**
 * Azure tenant id of the signed-in Office account, captured as a side effect
 * of the loginHint lookup. Unlike the persisted tenant id (written only after
 * a successful MSAL auth), this is available even when token acquisition never
 * succeeds - which is exactly when per-tenant fallback telemetry needs it.
 */
export function getOfficeTenantId(): string | null {
  return officeTenantId;
}

/**
 * Get the loginHint (user's UPN) from the Office host context.
 * Required for ssoSilent() in Word on the web.
 */
async function getLoginHint(): Promise<string | undefined> {
  try {
    if (typeof Office !== "undefined" && Office.auth?.getAuthContext) {
      const authContext = await Office.auth.getAuthContext();
      if (authContext.tenantId) {
        officeTenantId = authContext.tenantId;
      }
      if (authContext.userPrincipalName) {
        // Intentionally NOT logging the UPN itself — it's PII. Presence-only log.
        log.info("loginHint obtained from Office.auth.getAuthContext", {
          hasLoginHint: true,
        });
        return authContext.userPrincipalName;
      }
    }
  } catch (error) {
    log.warn("Could not get loginHint from Office context", {
      error: error instanceof Error ? error.message : String(error),
    });
  }
  return undefined;
}

/**
 * Initialize the MSAL PublicClientApplication for NAA.
 * Must be called once at startup before any token acquisition.
 * Returns the PCA instance, or null if initialization fails.
 */
export async function initializeMsal(): Promise<IPublicClientApplication | null> {
  if (pcaInstance) {
    log.info("MSAL PCA already initialized, reusing instance");
    return pcaInstance;
  }

  try {
    log.info("Initializing MSAL PCA via createNestablePublicClientApplication");
    pcaInstance = await createNestablePublicClientApplication(MSAL_CONFIG);
    log.info("MSAL PCA initialized");
    return pcaInstance;
  } catch (error) {
    log.error("MSAL PCA initialization failed", {
      error: error instanceof Error ? error.message : String(error),
    });
    return null;
  }
}

/**
 * Acquire a token for the first time (initial SSO).
 * 1. Gets loginHint from Office context (for Word on the web)
 * 2. Attempts ssoSilent — no user interaction needed
 * 3. On InteractionRequiredAuthError → throws InteractionRequiredFallbackError
 *    (no interactive prompt - the caller falls back to Legacy SSO silently)
 *
 * Throws on unrecoverable errors (caller should handle fallback to Legacy SSO).
 */
export async function acquireTokenInitial(): Promise<AuthenticationResult> {
  if (!pcaInstance) {
    throw new Error("MSAL PCA not initialized. Call initializeMsal() first.");
  }

  const loginHint = await getLoginHint();
  const tokenRequest = { scopes: MSAL_SCOPES, loginHint };

  try {
    // PII avoidance: log presence of loginHint, not the UPN itself.
    log.info("Attempting ssoSilent", {
      hasLoginHint: loginHint !== undefined,
      timeoutMs: SSO_SILENT_TIMEOUT_MS,
    });
    const result = await raceWithTimeout(
      pcaInstance.ssoSilent(tokenRequest),
      SSO_SILENT_TIMEOUT_MS,
      "ssoSilent"
    );
    activeAccount = result.account;
    // Persist the user's tenant id so the next MSAL init uses a tenant-pinned
    // authority (faster than `/common`). Safe to call on every success.
    if (result.account?.tenantId) persistTenantId(result.account.tenantId);
    // PII avoidance: do not log the resolved email at INFO. A runtime log-level
    // bump (window.setLogLevel("INFO")) should not leak user emails.
    log.info("ssoSilent succeeded");
    return result;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      // Deliberately no interactive prompt here - see InteractionRequiredFallbackError.
      log.warn("ssoSilent requires interaction; suppressing prompt, falling back to Legacy SSO", {
        errorCode: (error as { errorCode?: string }).errorCode,
        subError: (error as { subError?: string }).subError,
      });
      throw new InteractionRequiredFallbackError(error);
    }
    throw error;
  }
}

/**
 * Race a promise against a timeout. Rejects with `SilentTimeoutError` on
 * timeout — callers branch on `error instanceof SilentTimeoutError` for
 * telemetry classification. Not an `InteractionRequiredAuthError`: the caller
 * should treat this as "fall back to Legacy SSO", NOT as a signal to retry
 * interactively.
 */
function raceWithTimeout<T>(promise: Promise<T>, timeoutMs: number, label: string): Promise<T> {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timeoutPromise = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => {
      reject(new SilentTimeoutError(label, timeoutMs));
    }, timeoutMs);
  });
  return Promise.race([promise, timeoutPromise]).finally(() => {
    if (timeoutId !== undefined) clearTimeout(timeoutId);
  }) as Promise<T>;
}

/**
 * Acquire a token silently (for refresh / subsequent calls).
 * MSAL returns a cached token if valid, or silently refreshes via the refresh
 * token. Wrapped in `raceWithTimeout` so a hung refresh (network/AAD outage)
 * can't stall the UI indefinitely — throws `SilentTimeoutError` on timeout.
 * On `InteractionRequiredAuthError` → throws `InteractionRequiredFallbackError`
 * (no interactive prompt - the caller falls back to Legacy SSO silently).
 *
 * `options.claims` threads an MFA step-up `claims` parameter through to MSAL
 * (currently no caller wires this from a WWW-Authenticate header — the hook
 * exists so a future ticket can implement step-up without changing this API).
 */
export async function acquireTokenSilent(
  options: { claims?: string } = {}
): Promise<AuthenticationResult> {
  if (!pcaInstance) {
    throw new Error("MSAL PCA not initialized. Call initializeMsal() first.");
  }

  const tokenRequest = {
    scopes: MSAL_SCOPES,
    account: activeAccount ?? undefined,
    claims: options.claims,
  };

  try {
    const result = await raceWithTimeout(
      pcaInstance.acquireTokenSilent(tokenRequest),
      SSO_SILENT_TIMEOUT_MS,
      "acquireTokenSilent"
    );
    activeAccount = result.account;
    if (result.account?.tenantId) persistTenantId(result.account.tenantId);
    return result;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      // Deliberately no interactive prompt here - see InteractionRequiredFallbackError.
      // AuthContext responds by re-running the full auth cascade, which lands
      // on Legacy SSO silently.
      log.warn(
        "acquireTokenSilent requires interaction; suppressing prompt, falling back to Legacy SSO",
        {
          errorCode: (error as { errorCode?: string }).errorCode,
          subError: (error as { subError?: string }).subError,
        }
      );
      throw new InteractionRequiredFallbackError(error);
    }
    throw error;
  }
}

/**
 * Sign out the current MSAL session.
 *
 * `logoutRedirect` is preferred in MSAL but performs a top-level navigation,
 * which tears down the Office add-in. For in-iframe use we instead clear
 * MSAL's cache via `clearCache()` and drop our module-level references.
 * The user is signed out locally; they remain signed in to Office itself.
 */
export async function signOut(): Promise<void> {
  if (!pcaInstance) return;
  try {
    // MSAL's type surface exposes `clearCache` on the controller; cast to the
    // documented v5 method. Fallback to logoutRedirect if clearCache is absent.
    const pca = pcaInstance as IPublicClientApplication & {
      clearCache?: (options?: unknown) => Promise<void>;
    };
    if (typeof pca.clearCache === "function") {
      await pca.clearCache();
    }
  } catch (error) {
    log.warn("MSAL signOut cleanup failed", {
      error: error instanceof Error ? error.message : String(error),
    });
  }
  activeAccount = null;
}

/**
 * Reset module state. Used in tests.
 */
export function resetMsalState(): void {
  pcaInstance = null;
  activeAccount = null;
  officeTenantId = null;
}
